import { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  evaluatePassword,
  getStrengthColor,
  getSuggestionText,
} from '../../utils/passwordValidator';

/**
 * Standardized password field with visibility toggle and optional strength meter
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {string} props.value - Field value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler (for validation)
 * @param {string} props.error - Error message
 * @param {boolean} props.showStrengthMeter - Whether to show password strength indicator
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Object} props.sx - Additional styles
 */
const FormPasswordField = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  showStrengthMeter = false,
  required = false,
  disabled = false,
  sx,
  ...rest
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const handleToggleVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Handle error display
  const getErrorText = () => {
    if (!error) return '';

    if (typeof error === 'object' && error.key) {
      return t(error.key, error.params);
    }

    if (typeof error === 'string' && (error.startsWith('validation.') || error.startsWith('errors.'))) {
      return t(error);
    }

    return error;
  };

  // Get password strength if needed
  const passwordEval = showStrengthMeter && value ? evaluatePassword(value) : null;

  return (
    <Box sx={sx}>
      <TextField
        name={name}
        label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        type={showPassword ? 'text' : 'password'}
        required={required}
        disabled={disabled}
        fullWidth
        margin="normal"
        error={!!error}
        helperText={getErrorText()}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleToggleVisibility}
                  edge="end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        {...rest}
      />

      {/* Password Strength Meter */}
      {showStrengthMeter && value && value.length > 0 && passwordEval && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('validation.password_strength')}: {passwordEval.strength}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={passwordEval.percentage}
            color={getStrengthColor(passwordEval.strength)}
            sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
          />
          {passwordEval.suggestions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {passwordEval.suggestions.map((suggestion, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {'\u2022'} {getSuggestionText(suggestion, t)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FormPasswordField;
